/**
 * bridge/nfc_controller.js — "nfc" bridge component.
 *
 * Capability-tier bridge component: reads an NDEF tag (text or URL) and returns the
 * decoded payload to the web layer via callback. Stimulus identifier "bridge--nfc"
 * (glob-derived from this subdir); Strada component name "nfc" MUST match the iOS
 * class var name (native/ios/Chippie/Bridge/NFCComponent.swift) and the
 * Android-reached method name.
 *
 * Why it extends Strada's BridgeComponent: the iOS reader is reached via
 * this.send("read", ...), which only works on a connected bridge component inside
 * the iOS Hotwire Native shell. Extending BridgeComponent gives us that send()
 * transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport: on connect this controller registers a single readNfc()
 * impl set onto the window.ChippieBridge facade ({ ios, android, pwa }). The facade
 * (chippie-bridge.js) owns platform detection and picks the right impl per call.
 * Because reading is asynchronous, readNfc() takes a node-style callback
 * (err, result) where result is { value, kind } and kind is "uri" | "text".
 *   - ios:     this.send("read", {}, reply) -> NFCComponent.swift; the reply payload
 *              is { value, kind } | { cancelled } | { error }.
 *   - android: window.ChippieNative.readNfc() arms NfcAdapter reader mode; the native
 *              side resolves asynchronously by calling back into
 *              window.ChippieBridge._onNfcResult(json) (see registration below),
 *              which routes to the pending callback.
 *   - pwa:     uses the Web NFC API (NDEFReader, Chrome on Android) when present;
 *              otherwise returns an "unsupported" error.
 *
 * Template wiring:
 *   <button data-controller="bridge--nfc"
 *           data-action="bridge--nfc#read">Scan tag</button>
 * Or call window.ChippieBridge.readNfc((err, result) => { ... }) directly from any JS.
 *
 * The Android impl, its result router and the Web NFC path carry no per-instance
 * state, so they are installed at module load via registerStandalone() and
 * ChippieBridge.readNfc() resolves everywhere. Only the ios impl needs a live
 * Strada instance for this.send, so connect() overrides it and disconnect()
 * reverts to the "unsupported" placeholder. Registering solely on connect (as
 * this file used to) left ChippieBridge.readNfc UNDEFINED on Android and PWA as
 * well as everywhere the UA-gated BridgeComponent.connect() never fires —
 * callers got a TypeError rather than the documented (err, result) callback.
 * Notably the PWA path is the ONE place Web NFC actually works (Chrome on
 * Android), and it was unreachable.
 *
 * Patterned on the sibling barcode_scanner_controller.js.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

function invoke(callback, err, result) {
  if (typeof callback === "function") callback(err, result);
}

// Shared decoder for both the iOS reply and the Android router: the two native
// sides emit the same payload shape, so the branch order lives in one place.
function deliver(callback, data) {
  data = data || {};
  if (data.error) return invoke(callback, new Error(data.error));
  if (data.cancelled) return invoke(callback, null, null);
  invoke(callback, null, { value: data.value, kind: data.kind });
}

function androidReadNfc(callback) {
  if (!window.ChippieNative || !window.ChippieNative.readNfc) {
    return invoke(callback, new Error("unsupported"));
  }
  // Native resolves asynchronously by calling _onNfcResult(json); stash the
  // callback so that router can find it.
  window.ChippieBridge._pendingNfc = callback;
  try {
    window.ChippieNative.readNfc();
  } catch (_e) {
    window.ChippieBridge._pendingNfc = null;
    invoke(callback, new Error("unsupported"));
  }
}

// PWA path: Web NFC (Chrome on Android, https only). Reads one record then stops.
function pwaReadNfc(callback) {
  if (typeof window.NDEFReader === "undefined") {
    return invoke(callback, new Error("unsupported"));
  }
  let settled = false;
  const finish = (err, result) => {
    if (settled) return;
    settled = true;
    invoke(callback, err, result);
  };
  try {
    const reader = new NDEFReader();
    reader.scan().then(() => {
      reader.onreadingerror = () => finish(new Error("read-error"));
      reader.onreading = (e) => {
        for (const record of e.message.records) {
          if (record.recordType === "url") {
            const value = new TextDecoder().decode(record.data);
            return finish(null, { value, kind: "uri" });
          }
          if (record.recordType === "text") {
            const value = new TextDecoder(record.encoding || "utf-8").decode(record.data);
            return finish(null, { value, kind: "text" });
          }
        }
        finish(new Error("unsupported-record"));
      };
    }).catch((err) => finish(new Error(err && err.message ? err.message : "scan-failed")));
  } catch (_e) {
    finish(new Error("unsupported"));
  }
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;

  window.ChippieBridge.register("readNfc", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder reports unsupported rather than hanging the callback.
    ios: (callback) => invoke(callback, new Error("unsupported")),
    android: androidReadNfc,
    pwa: pwaReadNfc,
  });

  // Android result router: native calls window.ChippieBridge._onNfcResult(json)
  // from the WebView (evaluateJavascript) once a tag is read or the read fails.
  // The argument is a JSON string: { value, kind } | { cancelled: true } | { error }.
  window.ChippieBridge._onNfcResult = (json) => {
    const callback = window.ChippieBridge._pendingNfc;
    window.ChippieBridge._pendingNfc = null;
    if (!callback) return;
    let data = {};
    try { data = JSON.parse(json) || {}; } catch (_e) { /* malformed */ }
    deliver(callback, data);
  };
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "nfc";

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    registerStandalone();
    window.ChippieBridge.register("readNfc", {
      ios: (callback) => {
        this.send("read", {}, (message) => deliver(callback, message && message.data));
      },
      android: androidReadNfc,
      pwa: pwaReadNfc,
    });
  }

  disconnect() {
    // Revert the live-send ios impl to the standalone placeholder. Deleting the
    // facade method and nulling the router (the old behaviour) also took the
    // capability away from Android and PWA, which never needed this instance.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--nfc#read" trigger. Dispatches an "nfc:result" CustomEvent
  // on this element so a host page can react without owning the callback wiring; on
  // error it dispatches "nfc:error".
  read(event) {
    if (event) event.preventDefault();
    window.ChippieBridge.readNfc((err, result) => {
      if (err) {
        this.dispatch("error", { detail: { error: err.message } });
        return;
      }
      if (!result) return; // cancelled
      this.dispatch("result", { detail: result });
    });
  }
}
